/**
 * tolerantJsonParse — a small, layered JSON parser for LLM output.
 *
 * Background: OpenAI's `response_format: { type: 'json_object' }` is *mostly*
 * reliable, but when a string value contains code (regexes, Windows paths,
 * template literals, etc.) the model occasionally emits a LONE backslash —
 * `\d`, `\s`, `\p`, `\x`, `\$` — instead of the JSON-required double form.
 * JSON.parse aborts at that byte even though the rest of the document is
 * structurally fine. Snake eval at 2026-05-28 hit this in `notes`/`newCode`
 * fields several times per run.
 *
 * This helper tries strategies in order, only escalating when the previous
 * strategy fails. Each strategy is NON-DESTRUCTIVE: it operates only on
 * patterns that are *invalid JSON by spec*, so it cannot change the
 * semantics of an already-valid document.
 *
 *   1. strict       — JSON.parse(raw) as-is.                     [identity]
 *   2. fenced       — strip a leading/trailing ```json … ``` wrap. [identity inside]
 *   3. escaped      — double lone backslashes (\X → \\X) where X
 *                     is not in the JSON valid-escape set.       [invalid → valid]
 *
 * The returned `strategy` tells the caller which path won, so callers can
 * log how often repair was needed without raising it to an error.
 */

export type TolerantJsonStrategy = 'strict' | 'fenced' | 'escaped';

export interface TolerantJsonResult {
  value: unknown;
  /** Which strategy produced the parsed value. `strict` means no repair. */
  strategy: TolerantJsonStrategy;
}

export class TolerantJsonError extends Error {
  /** Last underlying JSON.parse error message (the deepest failure). */
  readonly cause: string;
  constructor(message: string, cause: string) {
    super(message);
    this.name = 'TolerantJsonError';
    this.cause = cause;
  }
}

const FENCE_RE = /^```(?:json)?\s*\n?([\s\S]*?)\n?```$/;

// Valid characters after `\` in a JSON string per RFC 8259 §7.
// Anything else following `\` is invalid JSON and is the model's mistake.
const VALID_JSON_ESCAPE = new Set(['"', '\\', '/', 'b', 'f', 'n', 'r', 't', 'u']);

/**
 * Double a backslash everywhere it appears followed by a char that is NOT
 * a valid JSON escape. Cannot alter the meaning of valid JSON because the
 * pattern it matches is already invalid by spec.
 *
 * Examples (showing literal bytes):
 *   raw:    {"newCode":"const r = /\d+/g"}        ← invalid JSON
 *   fixed:  {"newCode":"const r = /\\d+/g"}       ← valid JSON, same string
 *
 *   raw:    {"path":"C:\Users\app"}               ← invalid JSON
 *   fixed:  {"path":"C:\\Users\\app"}             ← valid JSON, same string
 *
 *   raw:    {"newCode":"line1\nline2"}            ← already valid (\n is valid)
 *   fixed:  {"newCode":"line1\nline2"}            ← unchanged
 */
export function escapeLoneBackslashes(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch !== '\\') {
      out += ch;
      continue;
    }
    const next = input[i + 1];
    if (next !== undefined && VALID_JSON_ESCAPE.has(next)) {
      // Valid escape sequence (\\ counts as valid because next is \).
      // Copy the pair as-is and skip the next char.
      out += ch + next;
      i++;
      continue;
    }
    // Lone backslash. Double it so the JSON parser sees a literal '\'.
    out += '\\\\';
  }
  return out;
}

export function tolerantJsonParse(raw: string): TolerantJsonResult {
  let lastError = '';

  // ── 1. strict ──
  try {
    return { value: JSON.parse(raw), strategy: 'strict' };
  } catch (err) {
    lastError = (err as Error).message;
  }

  // ── 2. fenced ──
  const trimmed = raw.trim();
  const fenced = FENCE_RE.exec(trimmed)?.[1];
  if (fenced !== undefined && fenced !== trimmed) {
    try {
      return { value: JSON.parse(fenced), strategy: 'fenced' };
    } catch (err) {
      lastError = (err as Error).message;
    }
  }

  // ── 3. escaped (operates on whichever of `raw` / `fenced` we last saw) ──
  const repairInput = fenced ?? raw;
  const repaired = escapeLoneBackslashes(repairInput);
  if (repaired !== repairInput) {
    try {
      return { value: JSON.parse(repaired), strategy: 'escaped' };
    } catch (err) {
      lastError = (err as Error).message;
    }
  }

  throw new TolerantJsonError(
    `JSON parse failed after strict, fenced, and escape-repair strategies: ${lastError}`,
    lastError,
  );
}
